<script setup lang="ts">
import AutoHeight from 'embla-carousel-auto-height'
import groupBy from 'lodash/groupBy'
import minBy from 'lodash/minBy'

interface Media {
  id: string
  media: Record<string, any>,
}

const store = useStore()
const { params, path } = useRoute()

const { baseUrl } = useRuntimeConfig().public
const { currencyIsoCode } = storeToRefs(store)

const { data: category } = await useAsyncData(`base:${params.slug}`, async () => {
  const category = await $fetch(`/store-api/url/${params.slug}`, {
    method: 'POST',
    body: {
      includeMediaExtension: true,
      includes: {
        category: [
          'id',
          'name',
          'description',
          'metaTitle',
          'metaDescription',
          'mediaExtension',
          'customFields.category_video',
          'customFields.category_youtubeId',
          'customFields.category_schema',
          'breadcrumbWithUrl'
        ]
      }
    },
    query: {
      url: path
    }
  })

  const { elements: products, total, aggregations } = await $fetch(`/store-api/product-listing/${category.id}`, {
    method: 'POST',
    body: {
      associations: {
        seoUrls: {},
        productReviews: {}
      },
      includes: {
        product_listing: [
          'elements',
          'total',
          'aggregations'
        ]
      }
    },
    query: {
      url: path
    }
  })

  return {
    ...category,
    products,
    total,
    aggregations
  }
})

const products = computed(() => category.value.products)

if (products.value.length === 0) {
  throw createError({ statusCode: 404, statusMessage: 'No products in base category' })
}

if (products.value.length === 1) {
  await navigateTo(`/${products.value[0].extensions.urlPrefix.prefix}/${products.value[0].seoUrls?.[0].seoPathInfo}`, { redirectCode: 301 })
}

const description = computed(() => category.value.description)
const breadcrumbs = computed(() => category.value.extensions.breadcrumbWithUrl.breadcrumbs)
const video = computed(() => category.value.customFields.category_video?.match(/(^|=|\/)([0-9A-Za-z_-]{11})(\/|&|$|\?|#)/)[2])
const youtube = computed(() => {
  const embed = description.value.indexOf('https://www.youtube.com/embed/')
  if (embed !== -1) {
    return description.value.substring(embed + 30, embed + 30 + 11)
  }
})
const youtubeId = computed(() => category.value.customFields.category_youtubeId)
const gallery = computed(() => category.value.extensions.mediaExtension.collection)
const videoCover = computed<Media>(() => gallery.value[0])
const total = computed(() => category.value.total)
const price = computed(() => category.value.aggregations.price)
const minPrice = computed(() => Number.parseFloat(price.value.min))
const maxPrice = computed(() => Number.parseFloat(price.value.max))
const places = computed(() => 
  products.value.map(({ id, customFields, seoUrls, extensions: { urlPrefix: { prefix } } }: any) => ({
    id,
    name: customFields.product_printPlace,
    position: JSON.parse(customFields.product_pin),
    link: `/${prefix}/${seoUrls?.[0].seoPathInfo}`,
    icon: '/map/marker.svg'
  }))
)

const reviews = computed<Array<any>>(() => {
  return products.value.flatMap((product: any) => product.productReviews.filter((review:any) => review.status).map((review:any) => ({...review,product})))
})

const reviewsTotalCount = computed(() => reviews.value.length)
const ratingAverage = computed(() => {
  if (reviewsTotalCount.value > 0) {
    return reviews.value.reduce((acc, { points }) => acc + points, 0) / reviewsTotalCount.value
  }

  return 0
})
const locations = computed(() => groupBy(products.value, 'customFields.product_seoLocation'))
const hasReviews = computed(() => reviews.value.length)
const crossSellingCategoryId = computed(() => breadcrumbs.value[breadcrumbs.value.length - 2]?.id)

const lowerPriceProduct = (products: Array<any>) => minBy(products, 'calculatedCheapestPrice.unitPrice')
const reviewsRef = ref<HTMLElement | null>(null)
const scrollToReviews = () => reviewsRef.value?.scrollIntoView({ behavior: 'smooth' })

const index = ref<number>(0)

useHead({
  titleTemplate: `${category.value.metaTitle} - %s`,
  meta: [{ hid: 'description', name: 'description', content: category.value.metaDescription }],
  link: [{ hid: 'canonical', rel: 'canonical', href: `${baseUrl}${path}` }]
})

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': baseUrl,
          name: 'Go-Racing'
        }
      },
      ...breadcrumbs.value.map(({ name, url, urlPrefix }: any, index: number) => ({
        '@type': 'ListItem',
        position: index + 2,
        item: {
          '@id': `${baseUrl}/${urlPrefix}/${url}`,
          name
        }
      }))
    ]
  }),
  defineProduct({
    '@type': 'Product',
    name: category.value.name,
    description: category.value.metaDescription,
    sku: category.value.id,
    brand: {
      '@type': 'Organization',
      name: 'Go-racing'
    },
    image: gallery.value.map(({ alt, title, url, metaData: { height, width } }: any) => ({
      '@type': 'ImageObject',
      url,
      contentUrl: url,
      description: alt,
      name: title,
      height,
      width
    })),
    url: `${baseUrl}${path}`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: ratingAverage.value,
      reviewCount: reviewsTotalCount.value
    },
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: minPrice.value,
      highPrice: maxPrice.value,
      priceCurrency: currencyIsoCode.value,
      offerCount: products.value.length,
      offers: products.value.map((product: any) => ({
        '@type': 'Offer',
        areaServed: product.customFields.product_seoLocation,
        url: `${baseUrl}/${product.extensions.urlPrefix.prefix}/${product.seoUrls?.[0].seoPathInfo}`,
        price: product.calculatedCheapestPrice.unitPrice,
        priceCurrency: currencyIsoCode.value
      }))
    } as any,
    review: reviews.value.map(({ content, points, createdAt, extensions: { customerInfo: { name } } }) => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name
      },
      datePublished: createdAt,
      reviewBody: content,
      reviewRating: {
        '@type': 'Rating',
        ratingValue: points
      }
    }))
  })
])

const LeafletMap = loadWhenVisible({
  loader: () => import('@/components/LeafletMap.vue')
})
</script>

<template>
  <div class="container mx-auto mb-[50px]">
    <Breadcrumbs :breadcrumbs="breadcrumbs" />
    <h1 class="text-30 font-medium mb-[10px]">
      {{ category.name }}
    </h1>
    <RatingDisplay
      as="button"
      :rating-average="ratingAverage"
      :reviews="reviewsTotalCount"
      class="mb-[10px]" 
      @click="scrollToReviews"
    />
    <div class="grid-base">
      <div class="[grid-area:gallery]">
        <Lightbox name="base-gallery" @update:modelValue="($index: number) => index = $index + (video ? 1 : 0)">
          <Carousel v-model="index" :options="{ loop: true }" class="relative">
            <Carousel.Slide v-if="video" class="shrink-0 basis-full">
              <Youtube :id="video" :img="videoCover.media.url" />
            </Carousel.Slide>

            <Carousel.Slide v-for="(image, imageIndex) in gallery" :key="image.id" class="shrink-0 basis-full">
              <NuxtLink :to="image.url" :title="image.title" data-lightbox data-pswp-width="740" data-pswp-height="493">
                <Img :src="image.url" width="740" height="493" :alt="image.alt" :loading="!video && imageIndex === 0 ? 'eager' : 'lazy'" />
              </NuxtLink>
            </Carousel.Slide>

            <template #addons="{ prev, next }">
              <button aria-label="Poprzedni slajd" class="z-1 text-16 bg-white/60 absolute w-10 h-10 top-1/2 -translate-y-1/2 cursor-pointer rounded flex items-center justify-center left-2" @click.stop="prev">
                <i class="icon-arrow-button -rotate-180" />
              </button>

              <button aria-label="Następny slajd" class="z-1 text-16 bg-white/60 absolute w-10 h-10 top-1/2 -translate-y-1/2 cursor-pointer rounded flex items-center justify-center right-2" @click.stop="next">
               <i class="icon-arrow-button" />
              </button>
            </template>
          </Carousel>
        </Lightbox>

        <Carousel role="tablist" :modelValue="index" :options="{ dragFree: true, containScroll: 'keepSnaps' }" class="relative mt-[5px] [--slide-spacing:5px]">
          <Carousel.Slide v-if="video" role="tab" :aria-selected="index === 0" class="shrink-0 basis-[28%] sm:basis-[26%] cursor-pointer transition-all ease-in-out duration-500 brightness-50 hover:brightness-100 aria-selected:brightness-100" @click="index = 0">
            <div class="relative h-full w-full flex items-center justify-center cursor-pointer">
              <div
                class="absolute flex items-center justify-center w-[30px] h-[20px] bg-youtube rounded-[15%/50%] cursor-pointer before:border-solid before:border-t-[6px] before:border-b-[6px] before:border-l-[12px] before:border-t-transparent before:border-r-transparent before:border-b-transparent before:border-l-white shadow-[rgb(0_0_0/60%)_0_0_30px]"
              />
              <Img :src="videoCover.url" width="740" height="493" :alt="videoCover.alt" loading="eager" />
            </div>
          </Carousel.Slide>
          
          <Carousel.Slide v-for="(image, imageIndex) in gallery" :key="image.id" role="tab" :aria-selected="index === imageIndex + (video ? 1 : 0)" class="shrink-0 basis-[28%] sm:basis-[26%] cursor-pointer transition-all ease-in-out duration-500 brightness-50 hover:brightness-100 aria-selected:brightness-100" @click="index = imageIndex + (video ? 1 : 0)">
            <Img :src="image.url" width="740" height="493" :alt="image.alt" loading="lazy" />
          </Carousel.Slide>
        </Carousel>
      </div>
      <div class="[grid-area:left-column]">
        <div class="px-[10px] my-[10px]">
          <div class="text-24 mb-[5px]">
            Cena od:
            <Price :value="minPrice" class="font-medium" />
          </div>
          <div class="flex items-end text-16">
            <span class="text-primary font-medium flex-1">Wybierz lokalizację</span>
            <small class="text-right">(spośród {{ total }} dostępnych)</small>
          </div>
        </div>
        <ul class="border border-gray-400 mb-[10px]">
          <li v-for="(products, location) in locations" :key="location" class="cursor-pointer">
            <NuxtLink v-if="products.length == 1" :to="`/${products[0].extensions.urlPrefix.prefix}/${products[0].seoUrls?.[0].seoPathInfo}`" class="flex items-center justify-between p-[10px] transition-colors duration-300 ease-in-out hover:bg-primary hover:text-white focus:bg-primary focus:text-white">
              <span>{{ location }}</span>
              <Price :value="products[0].calculatedCheapestPrice.unitPrice" />
            </NuxtLink>
            <div v-else class="group">
              <div tabindex="0" role="button" class="flex items-center justify-between p-[10px] transition-colors duration-300 ease-in-out group-hover:bg-secondary group-hover:text-white group-focus-within:bg-secondary group-focus-within:text-white">
                {{ location }}
                <span class="px-[10px] whitespace-nowrap">{{ products.length }} miejsca</span>
                <Price :value="lowerPriceProduct(products).calculatedCheapestPrice.unitPrice" />
              </div>
              <ul class="hidden group-hover:block group-focus-within:block">
                <li v-for="p in products" :key="p.id">
                  <NuxtLink :to="`/${p.extensions.urlPrefix.prefix}/${p.seoUrls?.[0].seoPathInfo}`" class="flex items-center before:font-['icomoon'] before:content-['\e901'] before:text-10 before:mr-[10px] py-[10px] px-[20px] transition-colors duration-300 ease-in-out hover:bg-primary hover:text-white focus:bg-primary focus:text-white">
                    <span class="flex-auto">{{ p.name }}</span>
                    <Price :value="p.calculatedCheapestPrice.unitPrice" />
                  </NuxtLink>
                </li>
              </ul>
            </div>
          </li>
        </ul>
        <PaymentMethods class="grayscale justify-evenly w-full py-[15px]" />
        <Disclosure class="p-[20px] bg-gray-200">
          <Disclosure.Button as="div" role="button" class="group w-full text-left flex items-center justify-between text-primary text-16 font-medium">
            Mapa dostępnych lokalizacji <i class="icon-arrow-button text-[15px] transition-transform md:hidden rotate-90 group-aria-expanded:-rotate-90" />
          </Disclosure.Button>
          <Disclosure.Panel #default="{ isOpen }">
            <Transition.Panel>
              <div v-show="isOpen" class="md:!block md:!h-auto">
                <div class="relative z-0 w-auto h-full mt-[20px]">
                  <ClientOnly>
                    <LeafletMap :places="places" class="min-h-[492px]" />
                  </ClientOnly>
                </div>
              </div>
            </Transition.Panel>
          </Disclosure.Panel>
        </Disclosure>
      </div>
      <div class="[grid-area:description]">
        <Seo :content="description"  class="mt-[15px]" />
        <LazyYoutube v-if="youtube" :id="youtube" class="mb-[20px]" />
        <LazyYoutube v-if="youtubeId" :id="youtubeId" class="mb-[20px]" />
        <h3 class="mb-[10px]">
          Voucher już po minucie na Twoim e-mailu!
        </h3>
        <Img src="/voucher-img/wizka_bner_top.jpg" alt="voucher na jazdę, prezent dla niego lub dla niej" width="740" height="251" />
      </div>
      <div v-if="hasReviews" ref="reviewsRef" class="[grid-area:reviews] mt-[30px]">
        <p class="text-primary text-16 font-medium py-[10px]">
          Opinie
        </p>
        <Carousel :plugins="[AutoHeight()]" :options="{ loop: true }" class="relative px-[30px]">
          <Carousel.Slide v-for="review in reviews" :key="review.id" class="shrink-0 basis-full">
            <NuxtLink :to="`/${review.product.extensions.urlPrefix.prefix}/${review.product.seoUrls?.[0].seoPathInfo}`" class="block text-primary">
              {{ review.product.name }}
            </NuxtLink>
            <IconRating :rating="review.points" class="text-18" />
            <div class="mt-[5px]">
              {{ review.content }}
            </div>
            <div class="mt-[5px] text-12 font-medium capitalize">
              #{{ review.extensions.customerInfo.name }}
            </div>
          </Carousel.Slide>
          <template #addons="{ prev, next }">
            <button aria-label="Poprzedni slajd" class="z-1 text-16 absolute top-1/2 w-[10px] h-full px-[10px] -translate-y-1/2 cursor-pointer flex items-center justify-center left-0" @click.stop="prev">
              <i class="icon-arrow-button -rotate-180" />
            </button>

            <button aria-label="Następny slajd" class="z-1 text-16 absolute top-1/2 w-[10px] h-full px-[10px] -translate-y-1/2 cursor-pointer flex items-center justify-center right-0" @click.stop="next">
              <i class="icon-arrow-button" />
            </button>
          </template>
        </Carousel>
      </div>
    </div>
    <CrossSelling :category-id="crossSellingCategoryId" class="mt-[30px]" />
  </div>
</template>

<style lang="scss" scoped>
.grid-base {
  @apply grid gap-x-[30px] auto-cols-fr;
  grid-template-areas:
    "gallery"
    "left-column"
    "description"
    "reviews";

  @screen md {
    @apply grid gap-x-[30px];
    grid-template-columns: minmax(0, 2fr) 1fr;
    grid-template-areas:
      "gallery left-column"
      "description left-column"
      "reviews left-column";
    grid-template-rows: auto auto 1fr;
  }
}
</style>
